import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { createAutoCompleteMetaDataByDomain } from "commons/ultilities/dynamic-form/auto-complete/providers/factory";
import { ObjectSchemaType } from "commons/ultilities/dynamic-form/hooks/use-form";
import { InputType } from "commons/ultilities/dynamic-form/models/control";
import { IForm } from "commons/ultilities/dynamic-form/models/form";
import { cloneDeep } from "lodash";

import { InspectionConclusionEnum } from "commons/enum/inspection";
import { numberNanable } from "commons/ultilities/validation";

import {
  IAddDualLangInspectionNote,
  IUpdateDualLangInspectionNote,
} from "mobx-stores/_models/inspection-note/dual-lang-inspection-note/dual-lang-inspection-note";
import { date, string } from "yup";
import {
  getInspectionConclusionStatus,
  getPrintColorInspectionOptions,
  getStorageTemperatureOptions,
} from "./share";
export const getAddDualLangInspectionNoteFormSchema = (
  form: IForm,
): ObjectSchemaType<IAddDualLangInspectionNote> => {
  const resolveCustomerId = () => form?.getControl("customerId").value ?? null;

  const customerAutoCompleteMetaData: ReturnType<
    typeof createAutoCompleteMetaDataByDomain
  > = createAutoCompleteMetaDataByDomain("customer");

  const orderAutoCompleteMetaData: ReturnType<
    typeof createAutoCompleteMetaDataByDomain
  > = createAutoCompleteMetaDataByDomain("order", () => ({
    customerId: resolveCustomerId(),
  }));

  return {
    inspectionDate: {
      gridSize: 6,
      label: "Ngày kiểm nghiệm",
      type: InputType.datePicker,
      validattionYupSchema: date().required(),
    },
    number: {
      gridSize: 6,
      label: "Số phiếu",
      type: InputType.text,
      validattionYupSchema: string().max(64),
    },
    customerId: {
      type: InputType.autoComplete,
      gridSize: 6,
      label: "Khách hàng",
      validattionYupSchema: string().required(),
      metaData: {
        ...(customerAutoCompleteMetaData || {}),
        autoCompleteDomain: "customer",
        autoCompleteMode: "paged",
      },
    },
    workOrderPOId: {
      type: InputType.autoComplete,
      gridSize: 6,
      label: "Lệnh sản xuất",
      validattionYupSchema: string().required(),
      metaData: {
        searchOptionItemsAsync: async (keyword: string) => {
          const customerId = resolveCustomerId();
          if (!customerId) return [];
          return await workOrderPoBusiness.searchWoPoAsOptionItems(
            keyword,
            customerId,
          );
        },
      },
    },
    purchaseOrderId: {
      type: InputType.autoComplete,
      gridSize: 6,
      label: "Đơn đặt hàng",
      validattionYupSchema: string().required(),
      metaData: {
        ...(orderAutoCompleteMetaData || {}),
        autoCompleteDomain: "order",
        autoCompleteMode: "paged",
        searchOptionItemsAsync: async (keyword: string) => {
          const customerId = resolveCustomerId();
          if (!customerId) return [];
          return (
            (await orderAutoCompleteMetaData?.searchOptionItemsAsync?.(
              keyword,
            )) || []
          );
        },
        searchOptionItemsPagedAsync: async (keyword, request) => {
          const customerId = resolveCustomerId();
          if (!customerId) {
            return {
              items: [],
              totalCount: 0,
            };
          }

          return (
            (await orderAutoCompleteMetaData?.searchOptionItemsPagedAsync?.(
              keyword,
              request,
            )) || {
              items: [],
              totalCount: 0,
            }
          );
        },
      },
    },
    deliveredQuantity: {
      gridSize: 6,
      label: "Số lượng giao hàng",
      type: InputType.number,
      validattionYupSchema: numberNanable(),
    },
    packagesQuantity: {
      gridSize: 6,
      label: "Số lượng thùng (kiện)",
      type: InputType.text,
      validattionYupSchema: string().max(50),
    },
    quantityPerPackage: {
      gridSize: 6,
      label: "Số Lượng /Thùng (Kiện)",
      type: InputType.number,
      validattionYupSchema: numberNanable(),
    },
    inspectionSamples: {
      gridSize: 6,
      label: "Số Lượng Mẫu Kiểm",
      type: InputType.text,
      validattionYupSchema: string().max(50),
    },
    storageTemperature: {
      gridSize: 6,
      label: "Điều Kiện Bảo Quản",
      type: InputType.select,
      optionItems: getStorageTemperatureOptions(),
      validattionYupSchema: string(),
    },
    expireDateText: {
      gridSize: 6,
      label: "Hạn sử dụng",
      type: InputType.text,
      validattionYupSchema: string(),
    },
    inspector: {
      gridSize: 6,
      label: "Người kiểm nghiệm",
      type: InputType.text,
      validattionYupSchema: string(),
    },
    note: {
      gridSize: 12,
      label: "Ghi chú",
      type: InputType.textArea,
      metaData: {
        rows: 2,
      },
      validattionYupSchema: string(),
    },

    items: {
      label: "Chi tiết mặt hàng",
      type: InputType.array,
      arraySchema: {
        productId: {
          label: "Mặt hàng",
          gridSize: 12,
          type: InputType.autoComplete,
          validattionYupSchema: string().required(),
          defaultValue: "",
        },
        paperType: {
          label: "Loại giấy:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        paperTypeInspecctionStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        paperTypeInspect: {
          label: "Kiểm nghiệm (g/m²)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        paperTypeResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        thickness: {
          label: "Độ dày:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        thicknessStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        thicknessInspect: {
          label: "Kiểm nghiệm (mm)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        thicknessResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        weight: {
          label: "Trọng lượng:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        weightStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        weightInspect: {
          label: "Kiểm nghiệm (gram)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        weightResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        length: {
          label: "Chiều dài:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        lengthStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        lengthInspect: {
          label: "Kiểm nghiệm (mm)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        lengthResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        width: {
          label: "Chiều rộng:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        widthStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        widthInspect: {
          label: "Kiểm nghiệm (mm)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        widthResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        height: {
          label: "Chiều cao:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        heightStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        heightInspect: {
          label: "Kiểm nghiệm (mm)",
          type: InputType.number,
          gridSize: 3,
          validattionYupSchema: numberNanable(),
        },
        heightResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        approval: {
          label: "Mẫu ký duyệt:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        approvalStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        approvalInspect: {
          label: "Kiểm nghiệm",
          type: InputType.text,
          gridSize: 3,
          defaultValue: "",
          validattionYupSchema: string(),
        },
        approvalResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        printColor: {
          label: "Màu sắc in ấn:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        printColorStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        printColorInspect: {
          label: "Kiểm nghiệm",
          type: InputType.select,
          gridSize: 3,
          defaultValue: "",
          validattionYupSchema: string(),
          optionItems: getPrintColorInspectionOptions(),
        },
        printColorResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        apperance: {
          label: "Ngoại quan:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        appearanceInspectionStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
        },
        appearanceInspect: {
          label: "Kiểm nghiệm",
          type: InputType.text,
          gridSize: 3,
          defaultValue: "",
          validattionYupSchema: string(),
        },
        appearanceResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        otherStandard: {
          label: "Tiêu chuẩn khác:",
          type: InputType.label,
          gridSize: 2,
          defaultValue: "",
        },
        otherStandardInspectionStandard: {
          label: "",
          type: InputType.label,
          gridSize: 4,
          defaultValue: "",
          validattionYupSchema: string(),
        },
        otherStandardInspect: {
          label: "Kiểm nghiệm",
          type: InputType.text,
          gridSize: 3,
          defaultValue: "",
          validattionYupSchema: string(),
        },
        otherStandardResult: {
          label: "Kết quả",
          type: InputType.select,
          gridSize: 3,
          validattionYupSchema: string().required(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
        conclusion: {
          label: "Kết luận",
          type: InputType.text,
          gridSize: 12,
          validattionYupSchema: string(),
          defaultValue: InspectionConclusionEnum.None,
          optionItems: getInspectionConclusionStatus(),
        },
      },
    },
  };
};

export const getEditDualLangInspectionNoteFormSchema = (
  form: IForm,
): ObjectSchemaType<IUpdateDualLangInspectionNote> => {
  const schema = getAddDualLangInspectionNoteFormSchema(form);
  const updateSchema: ObjectSchemaType<IUpdateDualLangInspectionNote> =
    cloneDeep(schema);
  updateSchema.id = {
    type: InputType.none,
  };
  updateSchema.workOrderId = {
    type: InputType.none,
  };
  updateSchema.items.arraySchema.id = {
    type: InputType.none,
  };
  return updateSchema;
};
